import { z } from 'zod';
import { NullableBusinessId } from './tax.js';
// ── Profit & Loss Units (יחידות רווח והפסד) ─────────────────────────────────
// A box with more than one location can split payroll into P&L units. Each unit
// has a name and a set of assigned locations (Arbox location NAMES — there's no
// stable location id in the reports). When enabled, a coach's pay is split per
// unit by each class's schedule location: the report shows one row per
// (coach × unit) with a "יחידת רווח והפסד" column, and a SEPARATE email +
// approval link is produced per (coach × unit).
//
// Money attribution:
//   • Location-based components (class pay, weekend bonus, class bonus, and
//     per-km travel) land in the unit of the meeting that produced them.
//   • Non-location pay (base salary, flat travel, conversion bonus, one-time
//     bonus) lands ONLY in the coach's PRIMARY unit — the unit where they earned
//     the most class pay this period (tie → lowest unit id; no classes → the box
//     default unit).
//
// `0` is the sentinel "no unit" id: used on every line/job/snapshot when the
// feature is OFF, so existing single-row-per-coach behaviour is unchanged.
/** The sentinel unit id meaning "no P&L unit" (feature off / whole coach). */
export const PNL_UNIT_NONE = 0;
export const PnlUnitSchema = z.object({
    /** Real DB id (>0). 0/absent ⇒ a not-yet-persisted unit in a save payload. */
    id: z.number().int(),
    name: z.string().trim().min(1).max(120),
    /** The box's default unit — receives non-location pay for coaches with no
     *  class activity. Exactly one unit is primary when the feature is enabled. */
    is_primary: z.boolean(),
    /** Arbox location names assigned to this unit. A location belongs to exactly
     *  one unit (enforced in the DB + on save). */
    locations: z.array(z.string().trim().min(1)),
    /** The unit's ח״פ — the legal entity a coach's invoice for this unit must be
     *  addressed to. null ⇒ fall back to the box's ח״פ (BoxSettings.business_id). */
    business_id: z.string().nullable().optional(),
});
export const PnlConfigSchema = z.object({
    enabled: z.boolean(),
    units: z.array(PnlUnitSchema),
});
/** Save payload (client → server). New units omit `id` (or send ≤0); the server
 *  assigns real ids. Validation that every box location is covered + that
 *  exactly one unit is primary happens server-side against the live catalog. */
export const PnlConfigSaveSchema = z.object({
    enabled: z.boolean(),
    units: z
        .array(z.object({
        id: z.number().int().optional(),
        name: z.string().trim().min(1).max(120),
        is_primary: z.boolean().optional(),
        locations: z.array(z.string().trim().min(1)),
        business_id: NullableBusinessId,
    }))
        .max(50),
});
export const EMPTY_PNL_CONFIG = { enabled: false, units: [] };
/** Normalize a location name for case/space-insensitive matching. */
export function normLocation(s) {
    return (s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}
/** Map of normalized location name → unit id, from a config. */
export function locationUnitMap(config) {
    const m = new Map();
    for (const u of config.units) {
        for (const loc of u.locations) {
            const k = normLocation(loc);
            if (k)
                m.set(k, u.id);
        }
    }
    return m;
}
/** The box default unit (primary flag, else lowest id). PNL_UNIT_NONE if none. */
export function defaultUnitId(config) {
    if (config.units.length === 0)
        return PNL_UNIT_NONE;
    const primary = config.units.find((u) => u.is_primary);
    if (primary)
        return primary.id;
    return Math.min(...config.units.map((u) => u.id));
}
/** Which unit a location belongs to; `fallbackUnitId` when unassigned (e.g. a
 *  location added in Arbox after the units were configured). */
export function unitForLocation(location, locToUnit, fallbackUnitId) {
    const k = normLocation(location);
    const u = k ? locToUnit.get(k) : undefined;
    return u ?? fallbackUnitId;
}
/** The coach's primary unit: the unit with the most class pay this period (tie →
 *  lowest id). No class pay anywhere → the box default unit. */
export function pickCoachPrimaryUnit(classPayByUnit, config) {
    let best = -1;
    let bestPay = -Infinity;
    for (const [unitId, pay] of classPayByUnit) {
        if (pay > bestPay || (pay === bestPay && (best === -1 || unitId < best))) {
            best = unitId;
            bestPay = pay;
        }
    }
    return best === -1 ? defaultUnitId(config) : best;
}
/** A unit's display name by id (null for PNL_UNIT_NONE / unknown). */
export function unitName(config, unitId) {
    if (unitId === PNL_UNIT_NONE)
        return null;
    return config.units.find((u) => u.id === unitId)?.name ?? null;
}
/** Strip the non-location components for a NON-primary unit: base salary and
 *  conversion bonus belong ONLY to the coach's primary unit. Weekend bonus +
 *  class bonus are per-meeting, so they stay (they land in the unit of the
 *  meeting that produced them). The volume bonus stays too: its ladder is walked
 *  with the LINE's own paid-class count, so each branch counts the classes taught
 *  there and pays its own tiers. Travel: flat travel (monthly / per-workday) is
 *  whole-coach and goes to the primary unit only, but per_km travel is a
 *  per-location cost — it follows each meeting's location into its unit, so it's
 *  kept here. The primary unit keeps the full components. */
export function stripNonLocationComponents(s) {
    return {
        ...s,
        base_salary: { type: 'none' },
        travel: s.travel.type === 'per_km' ? s.travel : { type: 'none' },
        conversion_bonus: { ...s.conversion_bonus, enabled: false },
    };
}
//# sourceMappingURL=pnl.js.map